import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, X } from 'lucide-react';
import { useBudgetStore, SavedBudget } from '../store/budgetStore';
import toast from 'react-hot-toast';

interface SaveBudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgetData: Omit<SavedBudget, 'id' | 'createdAt' | 'name'>;
}

export const SaveBudgetModal: React.FC<SaveBudgetModalProps> = ({
  isOpen,
  onClose,
  budgetData
}) => {
  const [budgetName, setBudgetName] = useState('');
  const saveBudget = useBudgetStore(state => state.saveBudget);

  const handleSave = () => {
    if (!budgetName.trim()) {
      toast.error('Please enter a budget name');
      return;
    }

    try {
      saveBudget({
        ...budgetData,
        name: budgetName.trim()
      });
      toast.success('Budget saved successfully');
      onClose();
      setBudgetName('');
    } catch (error) {
      toast.error('Failed to save budget');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50"
          >
            <div className="glass-card p-6 border border-emerald-500/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold">Save Budget</h3>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-800/50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">
                    Budget Name
                  </label>
                  <input
                    type="text"
                    value={budgetName}
                    onChange={(e) => setBudgetName(e.target.value)}
                    placeholder="Enter a name for your budget"
                    className="glass-input px-4 py-2 w-full rounded-lg"
                    autoFocus
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-slate-500/10 text-slate-300 rounded-lg hover:bg-slate-500/20 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    Save Budget
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};